import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Users, Wrench, Mail, Globe, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6"
          >
            About Crafters Market
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-stone-600 leading-relaxed"
          >
            At Crafters Market, we're passionate about supporting Australia's talented 
            handmakers and crafters. We believe that the time, effort, and creativity 
            you invest in crafting unique products should be valued, not taxed.
          </motion.p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 md:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-teal-100 rounded-full">
                <Heart className="h-6 w-6 text-teal-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">
                Our Mission
              </h2>
            </div>
            <p className="text-stone-600 leading-relaxed text-lg">
              That's why we created a platform where you can list and sell your handmade 
              goods without commission fees. Our goal is to empower makers to grow their 
              business, earn a fair income, and share their passion with the community.
            </p>
            <p className="text-stone-600 leading-relaxed text-lg mt-4">
              Whether you're a seasoned artisan or just starting out, we're here to help 
              you connect with local buyers and showcase your talents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-12 md:py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 md:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-amber-100 rounded-full">
                <Users className="h-6 w-6 text-amber-700" />
              </div>
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">
                A Community-Driven Approach
              </h2>
            </div>
            <p className="text-stone-600 leading-relaxed text-lg">
              Our team is made up of makers and creators ourselves, and we understand 
              the importance of community and fair play. That's why we're committed to 
              creating a space where creators can thrive, and where customers can trust 
              they're getting authentic, high-quality products.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trades Directory Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 md:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-100 rounded-full">
                <Wrench className="h-6 w-6 text-blue-700" />
              </div>
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">
                The Trades Directory
              </h2>
            </div>
            <p className="text-stone-600 leading-relaxed text-lg">
              As a bonus, we're also launching a trades directory, where you can find 
              trusted, high-quality tradies in your area. Our tradies are passionate 
              about doing a great job, and we're committed to promoting the best of the best.
            </p>
            <p className="text-stone-600 leading-relaxed text-lg mt-4">
              High-rated tradies will get extra visibility, while those who don't meet 
              our standards may lose their spot.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Join Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-stone-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-6">
              Join Our Community
            </h2>
            <p className="text-stone-600 leading-relaxed text-lg max-w-2xl mx-auto mb-4">
              We're building something special here, and we'd love for you to be a part of it. 
              Whether you're a maker, a tradie, or just someone who loves supporting local, 
              we invite you to join our community.
            </p>
            <p className="text-stone-600 leading-relaxed text-lg max-w-2xl mx-auto mb-8">
              Have questions, feedback, or ideas? Get in touch – we're all ears!
            </p>
            <p className="text-teal-600 font-medium text-lg italic mb-8">
              Together, let's create a more supportive, uplifting marketplace that's all 
              about celebrating Aussie creativity and craftsmanship.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 md:p-10 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-8">
              Contact Us
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
              <a 
                href="mailto:craftersmarketaus@gmail.com" 
                className="flex items-center gap-3 text-stone-600 hover:text-teal-600 transition-colors"
              >
                <div className="p-3 bg-stone-100 rounded-full">
                  <Mail className="h-5 w-5" />
                </div>
                <span className="text-lg">craftersmarketaus@gmail.com</span>
              </a>
              <a 
                href="https://www.craftersmarket.com.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-stone-600 hover:text-teal-600 transition-colors"
              >
                <div className="p-3 bg-stone-100 rounded-full">
                  <Globe className="h-5 w-5" />
                </div>
                <span className="text-lg">www.craftersmarket.com.au</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
